import React, { useState } from "react";
import { Button, Card, CardContent, Stack, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { FileGroup } from "./FileGroupView";
import { useActiveFixesStore } from "../hooks/useActiveFixesStore";
import { useFixConnection } from "../hooks/useFixConnection";
import { FixWithClaudeDialog } from "./FixWithClaudeDialog";

interface FileCardProps {
  file: FileGroup;
  onViewFile: (filePath: string) => void;
  onCopy: (file: FileGroup) => void;
  generatePrompt: (file: FileGroup) => string;
  onErrorsRefresh?: () => void;
}

export const FileCard: React.FC<FileCardProps> = ({
  file,
  onViewFile,
  onCopy,
  generatePrompt,
  onErrorsRefresh
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startFix, setStartFix] = useState(false);
  const fixStatus = useActiveFixesStore((state) => state.getFix(file.filePath));

  // Start SSE connection when startFix is true
  useFixConnection(file.filePath, generatePrompt(file), startFix);

  const handleFixClick = () => {
    const currentFix = useActiveFixesStore.getState().getFix(file.filePath);

    if (!currentFix || currentFix.status !== 'running') {
      // Start new fix
      setStartFix(true);
      setDialogOpen(true);
    } else {
      // View existing fix
      setDialogOpen(true);
    }
  };

  const buttonColor = !fixStatus ? 'primary' :
    fixStatus.status === 'running' ? 'info' :
    fixStatus.status === 'success' ? 'success' :
    'error';

  const buttonText = !fixStatus ? 'Fix All with Claude' :
    fixStatus.status === 'running' ? 'View Progress...' :
    fixStatus.status === 'success' ? 'Fixed ✓' :
    'Fix Failed ✗';

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {file.filePath}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {file.eslintCount > 0 && `${file.eslintCount} ESLint`}
            {file.eslintCount > 0 && file.typescriptCount > 0 && ", "}
            {file.typescriptCount > 0 && `${file.typescriptCount} TypeScript`}
            {" ("}
            {file.totalCount} total)
          </Typography>
          <Stack direction="row" spacing={2} mt={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onViewFile(file.filePath)}
            >
              View Errors
            </Button>
            <Button
              variant="contained"
              color="info"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => onCopy(file)}
            >
              Copy All for Claude
            </Button>
            <Button
              variant="contained"
              color={buttonColor}
              size="small"
              onClick={handleFixClick}
            >
              {buttonText}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Dialog shows output from store */}
      <FixWithClaudeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        filePath={file.filePath}
        onSuccess={() => {
          onErrorsRefresh?.();
        }}
      />
    </>
  );
};
